import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import MainFrameSidebar from "./MainFrameSidebar";
import MainFrameContent from "./MainFrameContent";
import ProfileModale from "../Profile/ProfileModale";
import SettingsModal from "../Settings/SettingsModal";
import PopupMex from "../Components/SavePopup";

const GEN_BACKEND_LINK = process.env.REACT_APP_GEN_BACKEND_LINK;

const MainFrameAdmin = () => {
    const navigate = useNavigate();
    const account_id = localStorage.getItem("account_id");

    const [loading, setLoading] = useState(true);
    const [userData, setUserData] = useState(null);
    const [openSidebar, setOpenSidebar] = useState(true);

    const [mcpData, setMcpData] = useState([]);
    const [loadingMcpData, setLoadingMcpData] = useState(false);

    const [appData, setAppData] = useState([]);
    const [loadingAppData, setLoadingAppData] = useState(false);

    const [orgData, setOrgData] = useState([]);
    const [loadingOrgData, setLoadingOrgData] = useState(false);

    const [singleOrgData, setSingleOrgData] = useState(null)
    const [loadingSingleOrg, setLoadingSingleOrg] = useState(false)

    const [showProfileModal, setShowProfileModal] = useState(false);
    const [showSettingsModal, setShowSettingsModal] = useState(false);
    const [workspaceType, setWorkspaceType] = useState("console")
    const [popupMex, setPopupMex] = useState({ active: false, type: null, message: "" })

    const [chatData, setChatData] = useState([])
    const [loadingChatData, setLoadingChatData] = useState(false)


    const fetchUserData = useCallback(async (isInitialLoad = false) => {
        try {
            if (isInitialLoad) setLoading(true);

            const response = await fetch(`${GEN_BACKEND_LINK}/get_session`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ account_id }),
            });

            if (!response.ok) throw new Error("Failed to fetch user session");

            const data = await response.json();
            setUserData(data);
        } catch (error) {
            console.error("Error fetching session:", error);
            navigate("/login");
        } finally {
            if (isInitialLoad) {
                setLoading(false);
            }
        }
    }, [account_id, navigate]);

    const fetchOrgData = useCallback(async (org_id) => {
        if (!account_id) return;
        setLoadingOrgData(true);
        try {
            const response = await fetch(`${GEN_BACKEND_LINK}/get_organizations`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ account_id: account_id }),
            });

            const result = await response.json();
            if (!response.ok) throw new Error(result.error || "Failed to fetch orgs");

            setOrgData(result.data || []);
        } catch (err) {
            console.error("Error fetching organizations:", err);
        } finally {
            setTimeout(() => setLoadingOrgData(false), 500);
        }
    }, [account_id]);

    const fetchMcpData = async (org_id) => {
        setLoadingMcpData(true);
        try {
            const response = await fetch(`${GEN_BACKEND_LINK}/get_mcp_directory`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({
                    limit: 20,
                    account_id,
                    org_id
                }),
            });

            const result = await response.json();

            if (result.error == "not_authorized") {
                setPopupMex({ active: true, type: "error", message: "Organization auth..." })
                setTimeout(() => {
                    setPopupMex({ active: false, type: null, message: "" })
                    navigate(`/console/organizations`)
                }, 5000);
            }

            if (!response.ok) {
                throw new Error(result.error || "Failed to fetch");
            }

            setMcpData(result.data || []);
        } catch (err) {
            console.error("Error fetching models:", err);
        } finally {
            sessionStorage.removeItem("org_id")
            setTimeout(() => {
                setLoadingMcpData(false);
            }, 500);
        }
    };

    const fetchAppData = async (org_id) => {
        setLoadingAppData(true);
        try {
            const response = await fetch(`${GEN_BACKEND_LINK}/get_apps`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({
                    account_id,
                    org_id
                }),
            });

            const result = await response.json();

            if (result.error == "not_authorized") {
                setPopupMex({ active: true, type: "error", message: "Organization auth..." })
                setTimeout(() => {
                    setPopupMex({ active: false, type: null, message: "" })
                    navigate(`/console/organizations`)
                }, 5000);
            }

            if (!response.ok) {
                throw new Error(result.error || "Failed to fetch");
            }

            setAppData(result.data || []);
        } catch (err) {
            console.error("Error fetching models:", err);
        } finally {
            sessionStorage.removeItem("org_id")
            setTimeout(() => {
                setLoadingAppData(false);
            }, 500);
        }
    };

    const fetchSingleOrganization = async (orgId) => {
        if (!account_id || !orgId) return;
        setLoadingSingleOrg(true);

        try {
            const response = await fetch(`${GEN_BACKEND_LINK}/get_organization`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({
                    account_id: account_id,
                    organization_id: orgId
                }),
            });

            const data = await response.json();

            if (data.error == "organization_not_found") {
                setPopupMex({ active: true, type: "error", message: "Organization auth..." })
                setTimeout(() => {
                    setPopupMex({ active: false, type: null, message: "" })
                    navigate(`/console/organizations`)
                }, 5000);
            }

            if (!response.ok) {
                throw new Error(data.error || "Failed to fetch");
            }

            setSingleOrgData(data.data || null);

        } catch (err) {
            console.error("Error fetching organizations:", err);
        } finally {
            setTimeout(() => {
                setLoadingSingleOrg(false);
            }, 500);
        }
    };

    const fetchChatHistory = async (org_id) => {
        setLoadingChatData(true)
        try {
            const response = await fetch(`${GEN_BACKEND_LINK}/get_chat_history`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({
                    account_id,
                    org_id,
                    type: org_id
                }),
            });

            const result = await response.json();

            if (result.error == "not_authorized") {
                setPopupMex({ active: true, type: "error", message: "Organization auth..." })
                setTimeout(() => {
                    setPopupMex({ active: false, type: null, message: "" })
                    navigate(`/console/organizations`)
                }, 5000);
            }

            if (!response.ok) {
                throw new Error(result.error || "Failed to fetch");
            }

            setChatData(result.data)
        } catch (err) {
            console.error("Error fetching models:", err);
            setLoadingChatData(false)
        } finally {
            setTimeout(() => {
                setLoadingChatData(false)
            }, 500);
        }
    };

    const toggleSidebar = () => {
        setOpenSidebar(prev => !prev);
    };

    useEffect(() => {
        if (!account_id) {
            navigate("/login");
            return;
        }

        fetchUserData();
        fetchOrgData();
    }, []);

    return (
        <div className="mainframe_admin">
            <PopupMex active={popupMex.active} type={popupMex.type} message={popupMex.message} />

            <div className="mainframe_navbar" />

            <div className="mainframe_container">
                <MainFrameSidebar
                    account_id={account_id}
                    openSidebar={openSidebar}
                    toggleSidebar={toggleSidebar}
                    loading={loading}
                    userData={userData}
                    setShowProfileModal={setShowProfileModal}
                    setShowSettingsModal={setShowSettingsModal}
                    setWorkspaceType={setWorkspaceType}
                    workspaceType={workspaceType}
                    singleOrgData={singleOrgData}
                    loadingSingleOrg={loadingSingleOrg}
                    fetchSingleOrganization={fetchSingleOrganization}
                    fetchChatHistory={fetchChatHistory}
                    chatData={chatData}
                    loadingChatData={loadingChatData}
                />

                <MainFrameContent
                    account_id={account_id}
                    userData={userData}
                    fetchUserData={fetchUserData}
                    orgData={orgData}
                    loadingOrgData={loadingOrgData}
                    fetchOrgData={fetchOrgData}
                    setMcpData={setMcpData}
                    mcpData={mcpData}
                    fetchMcpData={fetchMcpData}
                    loadingMcpData={loadingMcpData}
                    appData={appData}
                    fetchAppData={fetchAppData}
                    loadingAppData={loadingAppData}
                    setOpenSidebar={setOpenSidebar}
                    singleOrgData={singleOrgData}
                    fetchChatHistory={fetchChatHistory}
                    chatData={chatData}
                    loadingChatData={loadingChatData}
                    loadingSingleOrg={loadingSingleOrg}
                    fetchSingleOrganization={fetchSingleOrganization}
                />
            </div>

            <ProfileModale
                account_id={account_id}
                show={showProfileModal}
                handleClose={() => setShowProfileModal(false)}
                userData={userData}
                fetchRecords={fetchUserData}
            />

            <SettingsModal
                account_id={account_id}
                show={showSettingsModal}
                handleClose={() => setShowSettingsModal(false)}
                userData={userData}
                fetchRecords={fetchUserData}
            />
        </div>
    );
};

export default MainFrameAdmin;